import { useAppDispatch, useAppSelector } from "@app/hooks";
import { SortableAccordionGroup } from "@components/accordion/SortableAccordion";
import Switch from "@components/Switch";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { useDeleteSectionMutation } from "@services/survey-sections";
import { scrollToSection } from "@shared/utils";
import type { SurveySection } from "@survey-types/survey.section.types";
import { deleteSection as deleteSectionAction } from "@survey/surveySlice";
import { isUSUser } from "@utils/countryUtils";
import { FocusEvent, KeyboardEvent } from "react";
import {
  getSectionComponent,
  getSectionDisplayName,
} from "./sectionComponentMap";
import { useSectionEditing } from "./useSectionEditing";
import { useSectionOperations } from "./useSectionOperations";

const BuilderAccordionSection = () => {
  const dispatch = useAppDispatch();
  const { currentSurvey } = useAppSelector((state) => state.survey);
  const [deleteSection] = useDeleteSectionMutation();

  // Custom hooks for separated concerns
  const { handleUpdateSection, handleCreateNewSection, handleReorderByIndex } =
    useSectionOperations();

  const {
    lastCreatedSectionId,
    editingSection,
    titleError,
    handleSectionNameChange,
    handleSaveEdit,
    handleCancelEdit,
    handleNewSectionCreated,
    requestDeleteConfirmation,
  } = useSectionEditing();

  // Get sections from Redux
  const sections = currentSurvey?.sections || [];

  // Handle section deletion with confirmation
  const handleDelete = async (section: SurveySection) => {
    if (!currentSurvey?.id) return;

    const confirmed = await requestDeleteConfirmation(section.name);
    if (!confirmed) return;

    dispatch(deleteSectionAction({ sectionId: section.surveySectionId }));
    // Fire the mutation in the background
    deleteSection({
      sectionId: section.surveySectionId,
      surveyId: currentSurvey.id,
    });
  };

  // Handle keyboard events for section name editing
  const handleInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    sectionId: string,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit(sectionId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // Handle creating a new section
  const handleCreateSection = async () => {
    const newSectionId = await handleCreateNewSection();
    handleNewSectionCreated(newSectionId);
  };

  // Create accordion items from sections
  const createAccordionItems = () => {
    // Filter out lease comps sections for non-US users
    const filteredSections = sections.filter((section: SurveySection) => {
      const sectionNameLower = section.name
        ?.toLowerCase()
        .replace(/[_-]/g, " ");
      if (sectionNameLower === "lease comps" && !isUSUser()) {
        return false;
      }
      return true;
    });

    return filteredSections.map((section: SurveySection, index: number) => {
      const showDragHandle = section.isReorderable !== false;

      // Calculate the display number based on visible sections
      let displayNumber = "-";
      if (!section.isHidden) {
        // Count how many visible sections come before this one
        const visibleSectionsBefore = sections
          .slice(0, index)
          .filter((s) => !s.isHidden).length;
        displayNumber = (visibleSectionsBefore + 1).toString();
      }
      const titleNumberDisplay = (
        <span className="section-number">{displayNumber}</span>
      );
      // Create the title display component with section number
      // Use display name for predefined sections, actual name for custom sections
      const sectionTitle = section.isCustom
        ? section.name
        : getSectionDisplayName(section.name);

      const titleDisplay = (
        <div className="flex items-center gap-3">
          <span className="capitalize">{sectionTitle}</span>
        </div>
      );

      return {
        id: section.surveySectionId,
        titleNumber: titleNumberDisplay,
        title: titleDisplay,
        titleEditable: section.isCustom,
        titleEditValue: section.name, // Use just the section name for editing, not the JSX element
        titleInputClass: "section-name-input flex-1 emerald-standard",
        onTitleChange: (value: string) => {
          handleSectionNameChange(section.surveySectionId, value);
        },
        onTitleKeyDown: (
          e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => handleInputKeyDown(e, section.surveySectionId),
        onTitleBlur: (
          e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
        ) => handleSaveEdit(section.surveySectionId, e),
        titleError:
          editingSection === section.surveySectionId
            ? (titleError ?? undefined)
            : undefined,
        onToggle: (isOpen: boolean) => {
          if (isOpen) {
            // Scroll to the corresponding section using refs
            scrollToSection(section.surveySectionId);
          }
        },
        children: getSectionComponent(section.name, section),
        rightElement: (
          <div className="flex items-center gap-2">
            {section.isCustom && (
              <EmeraldIconButton
                icon="delete"
                type="Icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(section);
                }}
                aria-label="Delete section"
              />
            )}
            <Switch
              id={`section-${section.surveySectionId}`}
              checked={!section.isHidden}
              size="small"
              onChange={(isChecked: boolean) => {
                handleUpdateSection(
                  {
                    surveySectionId: section.surveySectionId,
                    isHidden: !isChecked,
                  },
                  sectionTitle,
                );
              }}
              aria-label={`${section.isHidden ? "Show" : "Hide"} ${sectionTitle} section`}
            />
          </div>
        ),
        defaultOpen: section.surveySectionId === lastCreatedSectionId,
        showDragHandle,
      };
    });
  };

  if (!currentSurvey) {
    return (
      <div className="p-4 text-center text-gray-500">No survey selected</div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-y-3" id="SC-DragSurveySections">
      <div className="space-y-2">
        <SortableAccordionGroup
          items={createAccordionItems()}
          onReorderByIndex={handleReorderByIndex}
          className="emerald-accordion-primary"
          allowMultiple
        />
      </div>

      <div className="mx-4">
        <EmeraldButton
          icon="add"
          label="New Section"
          type="TextButton"
          onClick={handleCreateSection}
        />
      </div>
    </div>
  );
};

BuilderAccordionSection.displayName = "BuilderAccordionSection";
export default BuilderAccordionSection;
